import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useGraphEditorStore } from '@/graph-store';

type TransferInsertRole = 'source' | 'destination';
type InsertChoice = 'before-transfer' | 'after-remainder' | 'after-merged';

export interface TransferInsertPositionModalProps {
  anchorMaterialId: string;
  transferOpId: string;
  role: TransferInsertRole;
  onClose: () => void;
}

interface ChoiceOption {
  value: InsertChoice;
  label: string;
  description: string;
}

export function TransferInsertPositionModal({
  anchorMaterialId,
  transferOpId,
  role,
  onClose,
}: TransferInsertPositionModalProps) {
  const operation = useGraphEditorStore((s) => s.graphData.operationNodes[transferOpId]);
  const openModal = useGraphEditorStore((s) => s.openModal);

  const [choice, setChoice] = useState<InsertChoice>('before-transfer');
  const [error, setError] = useState<string | null>(null);

  const options = useMemo<ChoiceOption[]>(() => {
    if (role === 'destination') {
      return [
        {
          value: 'before-transfer',
          label: 'Before transfer step',
          description: 'Insert between this pre-state material and the transfer step.',
        },
        {
          value: 'after-merged',
          label: 'After transfer (merged track)',
          description: 'Insert after the merged post-state material.',
        },
      ];
    }

    return [
      {
        value: 'before-transfer',
        label: 'Before transfer step',
        description: 'Insert between this pre-state material and the transfer step.',
      },
      {
        value: 'after-remainder',
        label: 'After transfer (remainder track)',
        description: 'Insert after the source remainder post-state material.',
      },
      {
        value: 'after-merged',
        label: 'After transfer (merged track)',
        description: 'Insert after the merged post-state material.',
      },
    ];
  }, [role]);

  const handleContinue = () => {
    const transferRow = operation?.transferRow;

    let resolvedAnchorId = anchorMaterialId;
    if (choice === 'after-remainder') {
      resolvedAnchorId = transferRow?.sourcePostMaterialId ?? '';
    } else if (choice === 'after-merged') {
      resolvedAnchorId = transferRow?.destinationPostMaterialId ?? '';
    }

    if (!resolvedAnchorId) {
      setError('Unable to determine selected insert position. Please choose another option.');
      return;
    }

    // Close this modal first, then open AddStepModal
    onClose();
    
    // Use setTimeout to ensure this modal closes before opening the next one
    setTimeout(() => {
      openModal({
        kind: 'insert-step',
        payload: { anchorMaterialId: resolvedAnchorId },
      });
    }, 0);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Select Insert Position
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Choose where the new step should be inserted relative to this transfer step.
          </Typography>

          {operation?.transferRow ? null : (
            <Alert severity="warning">
              Transfer mapping is incomplete. You can still insert before the transfer step.
            </Alert>
          )}

          <RadioGroup
            value={choice}
            onChange={(event) => {
              setChoice(event.target.value as InsertChoice);
              setError(null);
            }}
          >
            {options.map((option) => {
              const disabled =
                (option.value === 'after-remainder' && !operation?.transferRow?.sourcePostMaterialId) ||
                (option.value === 'after-merged' && !operation?.transferRow?.destinationPostMaterialId);

              return (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  disabled={disabled}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  }
                />
              );
            })}
          </RadioGroup>

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleContinue}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
